import React, { useEffect, useState } from "react";

import pdf from "@react-pdf/renderer";
import { observer } from "mobx-react-lite";

import MealPlanFormStore from "@/components/MealPlanForm/store/MealPlanFormStore";
import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";
import { percent, round } from "@/utils/number";
import { FragmentType } from "@/utils/type";
import Meat from "@assets/icons/beef.png";
import Fruit from "@assets/icons/fruits.png";
import Milk from "@assets/icons/milk.png";
import Rice from "@assets/icons/starch.png";
import Sugar from "@assets/icons/sugar-cube.png";
import Fat from "@assets/icons/trans-fats-free.png";
import Vegetable from "@assets/icons/vegetable.png";

import Footer from "./components/Footer";
import Flex from "./components/layout/Flex";
import Subheader from "./components/Subheader";
import {
  mulish,
  mulishBold,
  mulishExtraBold,
  mulishExtraLight,
  mulishLight,
  mulishMedium,
  mulishSemiBold,
} from "./fonts";

const { Page, StyleSheet, Font, View, Text, Image } = pdf;

Font.register({
  family: "Mulish",
  fonts: [
    {
      src: mulish,
      fontWeight: "normal",
    },
    {
      src: mulishBold,
      fontWeight: "bold",
    },
    {
      src: mulishExtraBold,
      fontWeight: "ultrabold",
    },
    {
      src: mulishExtraLight,
      fontWeight: "ultralight",
    },
    {
      src: mulishLight,
      fontWeight: "light",
    },
    {
      src: mulishMedium,
      fontWeight: "medium",
    },
    {
      src: mulishSemiBold,
      fontWeight: "semibold",
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: "0px",
    fontFamily: "Mulish",
    color: colors.dark,
    fontSize: "16px",
  },
  content: {
    padding: "16px",
    height: "80vh",
  },
  foodExchangeContainer: {
    width: "100%",
    padding: 0,
    borderTop: "2.4px solid #112340",
  },
  foodExchangeItemContainer: {
    flex: 1,
    padding: 12,
    border: "1px solid #EDF0F5",
  },
  foodExchangeItemTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: "#595F68",
  },
  foodExchangeSubItemTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: colors.gray,
  },
  calculationTableContainer: {
    backgroundColor: colors.dark,
    color: "white",
    width: "100%",
    marginTop: 24,
  },
  calculationTableHeaderContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 8,
  },
  calculationTableHeader: {
    fontWeight: 600,
    fontSize: 12,
    flex: 1,
  },
  calculationTableRowContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: colors.light,
    padding: 8,
  },
  calculationTableRow: {
    color: "#0B1B26",
    fontWeight: 700,
    fontSize: 12,
    flex: 1,
  },
  calculationTableRowCaption: {
    color: colors.gray,
  },
});

type Props = {
  data: FragmentType<typeof pdfFelTemplateFragment>;
};

type FoodExchangeItemProps = {
  title: string;
  value?: string | number;
  icon: string;
};

type FoodExchangeSubItemProps = {
  title: string;
  value: string | number;
};

const FoodExchangeItemContainer: React.FC = ({ children }) => {
  return <View style={styles.foodExchangeItemContainer}>{children}</View>;
};

const FoodExchangeItem: React.FC<FoodExchangeItemProps> = ({
  title,
  icon,
  value,
  children,
}) => {
  return (
    <View style={{ width: "100%" }}>
      <Flex>
        <Image src={icon} style={{ width: "16px", height: "16px" }} />
        <View style={{ marginLeft: 12, width: "100%" }}>
          <View style={{ width: "100%" }}>
            <Flex flexDirection="column">
              <Text style={styles.foodExchangeItemTitle}>{title}</Text>
              {children ? children : <Text>{round(Number(value), 2)}</Text>}
            </Flex>
          </View>
        </View>
      </Flex>
    </View>
  );
};

const FoodExchangeSubItem: React.FC<FoodExchangeSubItemProps> = ({
  title,
  value,
}) => {
  return (
    <View style={{ width: "100%" }}>
      <Flex alignItems="center" justifyContent="spaceBetween">
        <Text style={styles.foodExchangeSubItemTitle}>{title}</Text>
        <Text>{round(Number(value), 2)}</Text>
      </Flex>
    </View>
  );
};

const pdfFelTemplateFragment = graphql(`
  fragment PDFFelTemplate on Program {
    ...PDFMealPlanSubheader
    mealPlan {
      id
      macronutrientsCarbohydratePercent
      macronutrientsProteinPercent
      macronutrientsFatPercent
      hasFoodExchange
      foodExchangeVegetable
      foodExchangeFruit
      foodExchangeFat
      foodExchangeSugar
      foodExchangeMeatLowFat
      foodExchangeMeatModerateFat
      foodExchangeMeatHighFat
      foodExchangeMilkWhole
      foodExchangeMilkLowFat
      foodExchangeMilkNonFatSkim
      foodExchangeRiceLowProtein
      foodExchangeRiceMediumProtein
      foodExchangeRiceHighProtein
      foodExchangeVegetables
      foodExchangeTreats
      foodExchangeStarches
      foodExchangeDairy
      foodExchangeMeatAndSeafood
      foodExchangeFatsAndOil
      foodExchangeFruits
      foodExchangePlantBasedMeat
    }
  }
`);

const PDFFelTemplate: React.FC<Props> = observer(({ data }) => {
  // `store` is only used for calculations.
  const [store] = useState(new MealPlanFormStore("program"));
  useEffect(() => {
    store.setRecommendedCalories(
      data.mealPlan?.recommendedCalories.toString() ?? ""
    );
    store.macronutrients.set(
      "carbohydrates",
      data.mealPlan?.macronutrientsCarbohydratePercent?.toString() ?? ""
    );
    store.macronutrients.set(
      "proteins",
      data.mealPlan?.macronutrientsProteinPercent?.toString() ?? ""
    );
    store.macronutrients.set(
      "fats",
      data.mealPlan?.macronutrientsFatPercent?.toString() ?? ""
    );
    store.foodExchange.set(
      "fatsAndOil",
      data.mealPlan?.foodExchangeFatsAndOil?.toString() ?? ""
    );
    store.foodExchange.set(
      "fruits",
      data.mealPlan?.foodExchangeFruits?.toString() ?? ""
    );
    store.foodExchange.set(
      "meatAndSeafood",
      data.mealPlan?.foodExchangeMeatAndSeafood?.toString() ?? ""
    );
    store.foodExchange.set(
      "dairy",
      data.mealPlan?.foodExchangeDairy?.toString() ?? ""
    );
    store.foodExchange.set(
      "starches",
      data.mealPlan?.foodExchangeStarches?.toString() ?? ""
    );
    store.foodExchange.set(
      "treats",
      data.mealPlan?.foodExchangeTreats?.toString() ?? ""
    );
    store.foodExchange.set(
      "vegetables",
      data.mealPlan?.foodExchangeVegetables?.toString() ?? ""
    );
    store.foodExchange.set(
      "plantBasedMeat",
      data.mealPlan?.foodExchangePlantBasedMeat?.toString() ?? ""
    );
  }, [data]);
  const macros = store.macronutrients;
  const {
    totalCarbohydrateCalories,
    totalCarbohydrateGrams,
    totalCarbohydratePercent,
    totalFatCalories,
    totalFatGrams,
    totalFatPercent,
    totalProteinCalories,
    totalProteinGrams,
    totalProteinPercent,
  } = store.foodExchange.calculations;

  return (
    <Page size="A4" style={styles.page}>
      <Subheader title="FEL" data={data} />
      <View style={styles.content}>
        {/* Food exchange */}
        <View style={styles.foodExchangeContainer}>
          <Flex alignItems="center">
            <FoodExchangeItemContainer>
              <FoodExchangeItem
                title="Vegetable"
                value={data.mealPlan?.foodExchangeVegetables ?? 0}
                icon={Vegetable}
              />
            </FoodExchangeItemContainer>
            <FoodExchangeItemContainer>
              <FoodExchangeItem
                title="Fruit"
                value={data.mealPlan?.foodExchangeFruits ?? 0}
                icon={Fruit}
              />
            </FoodExchangeItemContainer>
            <FoodExchangeItemContainer>
              <FoodExchangeItem
                title="Sugar"
                value={data.mealPlan?.foodExchangeTreats ?? 0}
                icon={Sugar}
              />
            </FoodExchangeItemContainer>
            <FoodExchangeItemContainer>
              <FoodExchangeItem
                title="Fat"
                value={data.mealPlan?.foodExchangeFatsAndOil ?? 0}
                icon={Fat}
              />
            </FoodExchangeItemContainer>
          </Flex>
          <Flex alignItems="center">
            <FoodExchangeItemContainer>
              <FoodExchangeItem title="Dairy" icon={Milk}>
                <FoodExchangeSubItem
                  title="Dairy"
                  value={data.mealPlan?.foodExchangeDairy ?? 0}
                />
              </FoodExchangeItem>
            </FoodExchangeItemContainer>
            <FoodExchangeItemContainer>
              <FoodExchangeItem title="Starches" icon={Rice}>
                <FoodExchangeSubItem
                  title="Starches"
                  value={data.mealPlan?.foodExchangeStarches ?? 0}
                />
              </FoodExchangeItem>
            </FoodExchangeItemContainer>
            <FoodExchangeItemContainer>
              <FoodExchangeItem title="Meat" icon={Meat}>
                <FoodExchangeSubItem
                  title="Meat and Seafood"
                  value={data.mealPlan?.foodExchangeMeatAndSeafood ?? 0}
                />
                <FoodExchangeSubItem
                  title="Plant-based Meat"
                  value={data.mealPlan?.foodExchangeMeatModerateFat ?? 0}
                />
              </FoodExchangeItem>
            </FoodExchangeItemContainer>
          </Flex>

          {/* Calculation table */}
          {/* Table header */}
          <View style={styles.calculationTableContainer}>
            <View style={styles.calculationTableHeaderContainer}>
              <Text style={{ color: colors.dark, flex: 1 }}>Empty</Text>
              <Text style={styles.calculationTableHeader}>Grams</Text>
              <Text style={styles.calculationTableHeader}>Calories</Text>
              <Text style={styles.calculationTableHeader}>Percent</Text>
            </View>
          </View>
          {/* Table rows */}
          <View style={styles.calculationTableRowContainer}>
            {/* Carbs */}
            <Text style={styles.calculationTableRow}>Carbs</Text>
            <View style={styles.calculationTableRow}>
              <Flex alignItems="center">
                <Text>{round(totalCarbohydrateGrams, 2)} </Text>
                <Text style={styles.calculationTableRowCaption}>
                  {" "}
                  / {round(macros.carbohydrateGrams, 2)} g
                </Text>
              </Flex>
            </View>
            <View style={styles.calculationTableRow}>
              <Flex alignItems="center">
                <Text>{round(totalCarbohydrateCalories, 2)} </Text>
                <Text style={styles.calculationTableRowCaption}>
                  {" "}
                  / {round(macros.carbohydrateCalories, 2)} kcal
                </Text>
              </Flex>
            </View>
            <View style={styles.calculationTableRow}>
              <Flex alignItems="center">
                <Text>{percent(totalCarbohydratePercent, { round: 2 })}% </Text>
                <Text style={styles.calculationTableRowCaption}>
                  {" "}
                  / {round(+macros.carbohydrates, 2)}%
                </Text>
              </Flex>
            </View>
          </View>
          <View style={styles.calculationTableRowContainer}>
            {/* Proteins */}
            <Text style={styles.calculationTableRow}>Proteins</Text>
            <View style={styles.calculationTableRow}>
              <Flex alignItems="center">
                <Text>{round(totalProteinGrams, 2)} </Text>
                <Text style={styles.calculationTableRowCaption}>
                  {" "}
                  / {round(macros.proteinGrams, 2)} g
                </Text>
              </Flex>
            </View>
            <View style={styles.calculationTableRow}>
              <Flex alignItems="center">
                <Text>{round(totalProteinCalories, 2)} </Text>
                <Text style={styles.calculationTableRowCaption}>
                  {" "}
                  / {round(macros.proteinCalories, 2)} kcal
                </Text>
              </Flex>
            </View>
            <View style={styles.calculationTableRow}>
              <Flex alignItems="center">
                <Text>{percent(totalProteinPercent, { round: 2 })}% </Text>
                <Text style={styles.calculationTableRowCaption}>
                  {" "}
                  / {round(+macros.proteins, 2)}%
                </Text>
              </Flex>
            </View>
          </View>
          <View style={styles.calculationTableRowContainer}>
            {/* Fats */}
            <Text style={styles.calculationTableRow}>Fats</Text>
            <View style={styles.calculationTableRow}>
              <Flex alignItems="center">
                <Text>{round(totalFatGrams, 2)} </Text>
                <Text style={styles.calculationTableRowCaption}>
                  {" "}
                  / {round(macros.fatGrams, 2)} g
                </Text>
              </Flex>
            </View>
            <View style={styles.calculationTableRow}>
              <Flex alignItems="center">
                <Text>{round(totalFatCalories, 2)} </Text>
                <Text style={styles.calculationTableRowCaption}>
                  {" "}
                  / {round(macros.fatCalories, 2)} kcal
                </Text>
              </Flex>
            </View>
            <View style={styles.calculationTableRow}>
              <Flex alignItems="center">
                <Text>{percent(totalFatPercent, { round: 2 })}% </Text>
                <Text style={styles.calculationTableRowCaption}>
                  {" "}
                  / {round(+macros.fats, 2)}%
                </Text>
              </Flex>
            </View>
          </View>
        </View>
        <Footer />
      </View>
    </Page>
  );
});

export default PDFFelTemplate;
